import React from "react";
import { Card, CardContent } from "../ui/card";
import { ShieldCheck } from "lucide-react";
import { cn } from "../../lib/utils";

const GRADE_COLOR = {
  A: "text-green-600 bg-green-100 dark:bg-green-950/40",
  B: "text-green-600 bg-green-100 dark:bg-green-950/40",
  C: "text-amber-600 bg-amber-100 dark:bg-amber-950/40",
  D: "text-orange-600 bg-orange-100 dark:bg-orange-950/40",
  E: "text-red-600 bg-red-100 dark:bg-red-950/40",
};

const RADIUS = 62;
const CIRC = 2 * Math.PI * RADIUS;

export default function ReadinessCard({ data, testId = "readiness-card" }) {
  if (!data) return <div className="card-surface h-56 animate-pulse" />;
  const pct = Math.max(0, Math.min(100, data.score));
  const strokeColor =
    pct >= 90 ? "#16A34A" : pct >= 70 ? "#0891B2" : pct >= 50 ? "#D97706" : "#DC2626";
  const dash = (CIRC * pct) / 100;

  return (
    <Card data-testid={testId}>
      <CardContent className="p-6">
        <div className="flex flex-col md:flex-row items-center gap-6">
          <div className="relative w-40 h-40 shrink-0">
            <svg viewBox="0 0 160 160" className="w-full h-full -rotate-90">
              <circle cx="80" cy="80" r={RADIUS} stroke="hsl(var(--border))" strokeWidth="12" fill="none" />
              <circle
                cx="80" cy="80" r={RADIUS} stroke={strokeColor} strokeWidth="12" fill="none"
                strokeDasharray={`${dash} ${CIRC}`} strokeLinecap="round"
                style={{ transition: "stroke-dasharray 600ms ease" }}
              />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <div className="text-4xl font-semibold tabular-nums" style={{ color: strokeColor }}>{pct}</div>
              <div className="text-xs text-muted-foreground uppercase tracking-wider">Readiness</div>
              <div className={cn("mt-1 inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold", GRADE_COLOR[data.grade])}>
                {data.grade}
              </div>
            </div>
          </div>
          <div className="flex-1 min-w-0 w-full">
            <div className="flex items-center gap-2 mb-3">
              <ShieldCheck className="w-4 h-4 text-primary" />
              <div className="text-sm font-semibold">Audit Readiness Score</div>
            </div>
            <ul className="space-y-2.5">
              {(data.factors || []).map((f) => (
                <li key={f.key} data-testid={`readiness-factor-${f.key}`}>
                  <div className="flex items-center justify-between text-xs">
                    <div className="min-w-0">
                      <span className="font-medium text-foreground">{f.label}</span>{" "}
                      <span className="text-muted-foreground">· {f.detail}</span>
                    </div>
                    <span className="tabular-nums text-foreground font-semibold">{f.score}</span>
                  </div>
                  <div className="mt-1.5 h-1.5 bg-muted rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full"
                      style={{
                        width: `${f.score}%`,
                        backgroundColor:
                          f.score >= 80 ? "#16A34A" : f.score >= 50 ? "#0891B2" : f.score >= 30 ? "#D97706" : "#DC2626",
                      }}
                    />
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
